import {
  Activity,
  AlertCircle,
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  Clock,
  CreditCard,
  FileText,
  KeyRound,
  Loader2,
  Mail,
  Pencil,
  Power,
  Router,
  Search,
  ShieldCheck,
  Smartphone,
  Wallet,
  Wifi,
  X,
  XCircle,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useToast } from "../../components/ui/Toast";
import { API_CONFIG, fetchApp } from "../../services/apiService";

type PaymentStatus = "completed" | "pending" | "failed" | string;

interface PlanLite {
  id: string;
  name: string;
  price: number;
  speed?: string | null;
}

interface RouterLite {
  id: string;
  name: string;
  ip_address?: string | null;
}

interface BillingAccountLite {
  id: string;
  account_type: string;
  currency: string;
  balance_cents: number;
  is_active: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

interface InvoiceLite {
  id: string;
  invoice_number: string;
  status: string;
  currency: string;
  total_amount_cents: number;
  paid_amount_cents: number;
  period_start?: string | null;
  period_end?: string | null;
  created_at?: string | null;
}

interface PaymentLite {
  id: string;
  invoice_id?: string | null;
  billing_account_id: string;
  secret_id: string;
  amount_cents: number;
  currency: string;
  status: PaymentStatus;
  external_reference?: string | null;
  paid_at?: string | null;
  created_at?: string | null;
  payment_metadata?: Record<string, any> | null;
}

interface SecretDetail {
  id: string;
  username: string;
  email: string;
  phone_number: string;
  status: boolean;
  amount_payable?: number | null;
  account_number: string | number;
  plan_id: string | null;
  password?: string;
  router_id: string;
  olt_id?: string | null;
  created_at: string;
  updated_at: string;
  plan?: PlanLite | null;
  router?: RouterLite | null;
  billing_accounts?: BillingAccountLite[] | null;
  invoices?: InvoiceLite[];
  payments?: PaymentLite[];
  service_expires_at?: string | null;
  access_expires_at?: string | null;
  customer_id?: string;
}

interface ActiveConnectionDetail {
  id?: string | null;
  ".id"?: string | null;
  name?: string | null;
  service?: string | null;
  caller_id?: string | null;
  "caller-id"?: string | null;
  address?: string | null;
  uptime?: string | null;
  encoding?: string | null;
  session_id?: string | null;
  "session-id"?: string | null;
  limit_bytes_in?: string | number | null;
  "limit-bytes-in"?: string | number | null;
  limit_bytes_out?: string | number | null;
  "limit-bytes-out"?: string | number | null;
  radius?: string | boolean | null;
  comment?: string | null;
}

interface ActiveConnectionResponse {
  ok?: boolean;
  router_id?: string;
  router_name?: string;
  name?: string;
  active: boolean;
  message?: string;
  data?: ActiveConnectionDetail | null;
}

interface OltLite {
  id: string;
  name?: string | null;
  router_id?: string | null;
}

interface OnuDetailData {
  id?: string | null;
  name?: string | null;
  mac_address?: string | null;
  status?: string | null;
  status_raw?: string | number | null;
  distance_meters?: string | number | null;
  temperature?: string | number | null;
  tx_power?: string | number | null;
  rx_power?: string | number | null;
  pon?: string | number | null;
  matched_oid?: string | null;
  matched_raw_value?: string | null;
}

interface OnuByMacResponse {
  status: "found" | "not_found" | "error" | string;
  olt_id?: string;
  olt_name?: string;
  vendor?: string;
  model?: string;
  mac_address?: string;
  onu_index?: string;
  onu?: OnuDetailData | null;
  message?: string;
  error?: string;
}

const inputCls =
  "h-10 w-full rounded-2xl border border-slate-300 bg-slate-50 px-3.5 text-[13px] font-medium text-slate-700 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const textareaCls =
  "w-full resize-none rounded-2xl border border-slate-300 bg-slate-50 px-3.5 py-3 text-[13px] font-medium leading-relaxed text-slate-700 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const btnBase =
  "inline-flex items-center justify-center gap-2 rounded-2xl text-[13px] font-black transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]";

const formatKES = (value?: number | null) => {
  if (value === null || value === undefined) return "KES 0";
  return `KES ${new Intl.NumberFormat("en-KE").format(Number(value || 0))}`;
};

const formatCentsKES = (value?: number | null) => {
  if (value === null || value === undefined) return "KES 0";
  return `KES ${new Intl.NumberFormat("en-KE").format(Number(value || 0) / 100)}`;
};

const formatDate = (value?: string | null) => {
  if (!value) return "—";

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) return "—";

  return d.toLocaleDateString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "—";

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) return "—";

  return d.toLocaleString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatText = (value?: string | null) =>
  String(value || "—")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getInitials = (name: string) =>
  String(name || "")
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "C";

const AVATAR_PALETTES = [
  "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200",
  "bg-sky-100 text-sky-700 ring-1 ring-sky-200",
  "bg-violet-100 text-violet-700 ring-1 ring-violet-200",
  "bg-amber-100 text-amber-700 ring-1 ring-amber-200",
  "bg-teal-100 text-teal-700 ring-1 ring-teal-200",
  "bg-rose-100 text-rose-700 ring-1 ring-rose-200",
];

const avatarColor = (name: string) =>
  AVATAR_PALETTES[
    (name || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0) %
      AVATAR_PALETTES.length
  ];

const invoiceStatusStyle = (status: string) => {
  switch (status.toUpperCase()) {
    case "PAID":
      return "border-emerald-200 bg-emerald-100 text-emerald-800";
    case "PENDING":
      return "border-amber-200 bg-amber-100 text-amber-800";
    case "EXPIRED":
      return "border-slate-300 bg-slate-200 text-slate-600";
    case "CANCELLED":
      return "border-rose-200 bg-rose-100 text-rose-700";
    default:
      return "border-slate-300 bg-slate-200 text-slate-600";
  }
};

const paymentStatusStyle = (status: string) => {
  switch (status.toLowerCase()) {
    case "completed":
      return "border-emerald-200 bg-emerald-100 text-emerald-800";
    case "pending":
      return "border-amber-200 bg-amber-100 text-amber-800";
    case "failed":
      return "border-rose-200 bg-rose-100 text-rose-700";
    default:
      return "border-slate-300 bg-slate-200 text-slate-600";
  }
};

const getConnectionValue = (
  data: ActiveConnectionDetail | null | undefined,
  ...keys: (keyof ActiveConnectionDetail)[]
) => {
  if (!data) return "—";

  for (const key of keys) {
    const value = data[key];

    if (value !== undefined && value !== null && value !== "") {
      return String(value);
    }
  }

  return "—";
};

const normalizeMacForApi = (value?: string | null) => {
  if (!value) return "";

  const cleaned = String(value)
    .replace(/[^0-9a-fA-F]/g, "")
    .toUpperCase();

  if (cleaned.length !== 12) return "";

  return cleaned.match(/.{1,2}/g)?.join(":") || "";
};

const getCallerMacFromConnection = (
  data: ActiveConnectionDetail | null | undefined,
) => {
  const caller = getConnectionValue(data, "caller_id", "caller-id");

  if (!caller || caller === "—") return "";

  return normalizeMacForApi(caller);
};

const formatOnuUnit = (value?: string | number | null, unit = "") => {
  if (value === null || value === undefined || value === "") return "—";

  return `${value}${unit ? ` ${unit}` : ""}`;
};

const LoadingState: React.FC = () => (
  <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
    <div className="mx-auto flex min-h-[60vh] max-w-[1600px] items-center justify-center">
      <div className="w-full max-w-md overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
        <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

        <div className="relative overflow-hidden px-6 py-10 text-center">
          <div className="absolute -right-16 -top-20 h-52 w-52 rounded-full bg-emerald-200/35 blur-2xl" />
          <div className="absolute -bottom-20 left-4 h-52 w-52 rounded-full bg-cyan-200/35 blur-2xl" />

          <div className="relative mx-auto flex h-16 w-16 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-200/50">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          </div>

          <p className="relative mt-4 text-[15px] font-black text-slate-900">
            Loading customer details…
          </p>

          <p className="relative mt-1 text-[12.5px] font-medium text-slate-500">
            Preparing service details, billing history, and active connection
            status.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const ServiceBadge: React.FC<{ active?: boolean }> = ({ active }) => {
  if (active) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-100 px-3 py-1 text-[11px] font-black text-emerald-800">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
        Active
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-200 px-3 py-1 text-[11px] font-black text-slate-600">
      <span className="h-1.5 w-1.5 rounded-full bg-slate-500" />
      Inactive
    </span>
  );
};

const ActiveConnectionBadge: React.FC<{
  loading?: boolean;
  result?: ActiveConnectionResponse | null;
}> = ({ loading, result }) => {
  if (loading) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-100 px-3 py-1 text-[11px] font-black text-sky-800">
        <Loader2 className="h-3 w-3 animate-spin" />
        Checking
      </span>
    );
  }

  if (!result) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-200 px-3 py-1 text-[11px] font-black text-slate-600">
        Not checked
      </span>
    );
  }

  if (result.active) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-100 px-3 py-1 text-[11px] font-black text-emerald-800">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
        Online
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-100 px-3 py-1 text-[11px] font-black text-rose-700">
      <XCircle className="h-3 w-3" />
      Offline
    </span>
  );
};

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub: string;
  icon: React.ReactNode;
  tone: "slate" | "emerald" | "sky" | "amber" | "rose";
}> = ({ label, value, sub, icon, tone }) => {
  const styles = {
    slate: {
      card: "border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 shadow-slate-300/40",
      icon: "bg-slate-200 text-slate-700",
      text: "text-slate-600",
      bubble: "bg-slate-200/60",
    },
    emerald: {
      card: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-emerald-200/50",
      icon: "bg-emerald-100 text-emerald-700",
      text: "text-emerald-700",
      bubble: "bg-emerald-200/50",
    },
    sky: {
      card: "border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 shadow-sky-200/50",
      icon: "bg-sky-100 text-sky-700",
      text: "text-sky-700",
      bubble: "bg-sky-200/50",
    },
    amber: {
      card: "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-amber-200/50",
      icon: "bg-amber-100 text-amber-700",
      text: "text-amber-700",
      bubble: "bg-amber-200/50",
    },
    rose: {
      card: "border-rose-200 bg-gradient-to-br from-rose-50 to-red-50 shadow-rose-200/50",
      icon: "bg-rose-100 text-rose-700",
      text: "text-rose-700",
      bubble: "bg-rose-200/50",
    },
  }[tone];

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border px-5 py-5 shadow-sm ${styles.card}`}
    >
      <div
        className={`absolute -right-10 -top-10 h-28 w-28 rounded-full ${styles.bubble}`}
      />

      <div className="relative flex items-center gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${styles.icon}`}
        >
          {icon}
        </div>

        <div className="min-w-0">
          <p
            className={`text-[11px] font-black uppercase tracking-widest ${styles.text}`}
          >
            {label}
          </p>

          <p className="mt-0.5 truncate text-[20px] font-black tracking-tight text-slate-900 sm:text-[24px]">
            {value}
          </p>

          <p className={`text-[11px] font-bold ${styles.text}`}>{sub}</p>
        </div>
      </div>
    </div>
  );
};

const InfoCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  badge?: React.ReactNode;
  children: React.ReactNode;
}> = ({ icon, title, subtitle, badge, children }) => (
  <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/25">
    <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-slate-100/80 px-4 py-4 sm:px-5">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
          {icon}
        </span>

        <div>
          <h3 className="text-[14px] font-black text-slate-900">{title}</h3>

          {subtitle && (
            <p className="mt-0.5 text-[11.5px] font-medium text-slate-500">
              {subtitle}
            </p>
          )}
        </div>
      </div>

      {badge}
    </div>

    <div className="p-4 sm:p-5">{children}</div>
  </div>
);

const DetailItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  tone?: "slate" | "emerald" | "sky" | "amber" | "rose";
}> = ({ icon, label, value, tone = "slate" }) => {
  const tones = {
    slate: "border-slate-200 bg-slate-100",
    emerald: "border-emerald-200 bg-emerald-50",
    sky: "border-sky-200 bg-sky-50",
    amber: "border-amber-200 bg-amber-50",
    rose: "border-rose-200 bg-rose-50",
  };

  return (
    <div className={`rounded-2xl border px-3 py-3 ${tones[tone]}`}>
      <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-500">
        <span className="text-slate-400">{icon}</span>
        {label}
      </div>

      <div className="mt-1 break-words text-[13px] font-black text-slate-900">
        {value || "—"}
      </div>
    </div>
  );
};

const EmptyState: React.FC<{
  icon: React.ReactNode;
  title: string;
  message: string;
}> = ({ icon, title, message }) => (
  <div className="px-4 py-12 text-center">
    <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200 text-slate-500">
      {icon}
    </div>

    <p className="text-[14px] font-black text-slate-800">{title}</p>

    <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
      {message}
    </p>
  </div>
);

const CustomerDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();

  const returnTo =
    (location.state as { from?: string } | null)?.from || "/customers";

  const handleBackToCustomers = () => {
    navigate(returnTo, { replace: true });
  };

  const [customer, setCustomer] = useState<SecretDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [extendOpen, setExtendOpen] = useState(false);
  const [extendDays, setExtendDays] = useState("3");
  const [extendReason, setExtendReason] = useState("");
  const [extendLoading, setExtendLoading] = useState(false);

  const [activeName, setActiveName] = useState("");
  const [activeLookupLoading, setActiveLookupLoading] = useState(false);
  const [activeConnectionResult, setActiveConnectionResult] =
    useState<ActiveConnectionResponse | null>(null);
  const [onuLookupLoading, setOnuLookupLoading] = useState(false);
  const [onuLookupResult, setOnuLookupResult] =
    useState<OnuByMacResponse | null>(null);

  const resolveOltIdForCustomer = async (
    targetCustomer: SecretDetail,
  ): Promise<string | null> => {
    const directOltId =
      targetCustomer.olt_id ||
      (targetCustomer as any).olt_id ||
      (targetCustomer as any).olt?.id;

    if (directOltId) return String(directOltId);

    if (!targetCustomer.router_id) return null;

    const params = new URLSearchParams({
      router_id: targetCustomer.router_id,
    });

    const raw: any = await fetchApp(
      `${API_CONFIG.baseUrl}/olts?${params.toString()}`,
    );

    const olts: OltLite[] = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.data)
        ? raw.data
        : Array.isArray(raw?.items)
          ? raw.items
          : [];

    return olts[0]?.id || null;
  };

  const fetchOnuDetailsFromActiveConnection = async (
    targetCustomer: SecretDetail,
    activeResult: ActiveConnectionResponse,
    showErrors = false,
  ) => {
    const connection = activeResult.data || null;
    const callerMac = getCallerMacFromConnection(connection);

    setOnuLookupResult(null);

    if (!activeResult.active || !callerMac) {
      return;
    }

    setOnuLookupLoading(true);

    try {
      const oltId = await resolveOltIdForCustomer(targetCustomer);

      if (!oltId) {
        const message =
          "No OLT is linked to this customer/router. Add an OLT for this router first.";

        setOnuLookupResult({
          status: "error",
          mac_address: callerMac,
          error: message,
        });

        if (showErrors) {
          showToast(message, "error");
        }

        return;
      }

      const macWithoutLastOctet = callerMac.split(":").slice(0, -1).join(":");

      const params = new URLSearchParams({
        mac: macWithoutLastOctet,
      });

      const res: OnuByMacResponse = await fetchApp(
        `${API_CONFIG.baseUrl}/olts/${oltId}/onus/by-mac?mac=${macWithoutLastOctet}`,
      );
      setOnuLookupResult(res);
    } catch (e: any) {
      const message = e?.message || "Failed to fetch ONU details from OLT";

      setOnuLookupResult({
        status: "error",
        mac_address: callerMac,
        error: message,
      });

      if (showErrors) {
        showToast(message, "error");
      }
    } finally {
      setOnuLookupLoading(false);
    }
  };

  const checkActiveConnection = async (
    targetCustomer: SecretDetail,
    nameToCheck: string | number,
    showErrors = true,
  ) => {
    const routerId = targetCustomer.router_id;
    const name = nameToCheck.toString().trim();

    if (!routerId || !name) {
      if (showErrors) {
        showToast("Router and PPPoE username are required.", "error");
      }

      return;
    }

    setActiveLookupLoading(true);
    setOnuLookupLoading(false);
    setOnuLookupResult(null);

    try {
      const params = new URLSearchParams({
        router_id: routerId,
        name,
      });

      const res: ActiveConnectionResponse = await fetchApp(
        `${API_CONFIG.baseUrl}/secrets/active/detail?${params.toString()}`,
      );

      setActiveConnectionResult(res);

      // Do not await the HIOSO/OLT lookup.
      // The active connection card should render immediately, while the ONU
      // section shows its own loader until the HIOSO request finishes.
      if (res.active && res.data) {
        void fetchOnuDetailsFromActiveConnection(
          targetCustomer,
          res,
          showErrors,
        );
      }
    } catch (e: any) {
      const message = e?.message || "Failed to check active connection";

      setActiveConnectionResult({
        active: false,
        message,
        data: null,
      });

      setOnuLookupResult(null);

      if (showErrors) {
        showToast(message, "error");
      }
    } finally {
      setActiveLookupLoading(false);
    }
  };

  const loadCustomer = async () => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const data: SecretDetail = await fetchApp(
        `${API_CONFIG.baseUrl}/customers/${id}`,
      );

      setCustomer(data);
      setActiveName(data.account_number.toString());
      setActiveConnectionResult(null);
      setOnuLookupResult(null);

      // Auto-check active connection in the background.
      // This prevents slow HIOSO/OLT SNMP lookups from blocking the customer page.
      if (data.router_id && data.account_number) {
        void checkActiveConnection(data, data.account_number, false);
      }
    } catch (e: any) {
      setError(e?.message || "Failed to load customer");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleToggleStatus = async () => {
    if (!customer) return;

    setActionLoading(true);

    try {
      await fetchApp(
        `${API_CONFIG.baseUrl}/secrets/${customer.id}/toggle-status`,
        { method: "POST" },
      );

      const nextStatus = !customer.status;

      setCustomer((prev) => (prev ? { ...prev, status: nextStatus } : prev));

      showToast(
        `Customer ${customer.status ? "disabled" : "enabled"} successfully`,
        "success",
      );
    } catch (e: any) {
      showToast(e?.message || "Failed to update status", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleExtendAccess = async () => {
    if (!customer) return;

    const days = parseInt(extendDays, 10);

    if (!days || days < 1) {
      showToast("Please enter a valid number of days", "error");
      return;
    }

    setExtendLoading(true);

    try {
      const res = await fetchApp(
        `${API_CONFIG.baseUrl}/secrets/${customer.id}/extend`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ days, reason: extendReason || undefined }),
        },
      );

      setCustomer((prev) =>
        prev
          ? {
              ...prev,
              access_expires_at:
                res.new_access_expires_at ?? prev.access_expires_at,
            }
          : prev,
      );

      showToast(`Access extended by ${days} days`, "success");
      setExtendOpen(false);
      setExtendReason("");
    } catch (e: any) {
      showToast(e?.message || "Failed to extend access", "error");
    } finally {
      setExtendLoading(false);
    }
  };

  const handleCheckActiveConnection = async () => {
    if (!customer) return;
    await checkActiveConnection(customer, activeName, true);
  };

  const recentInvoices = useMemo(() => {
    if (!customer?.invoices?.length) return [];

    return [...customer.invoices].sort(
      (a, b) =>
        new Date(b.created_at || b.period_start || "").getTime() -
        new Date(a.created_at || a.period_start || "").getTime(),
    );
  }, [customer]);

  const recentPayments = useMemo(() => {
    if (!customer?.payments?.length) return [];

    return [...customer.payments].sort(
      (a, b) =>
        new Date(b.paid_at || b.created_at || "").getTime() -
        new Date(a.paid_at || a.created_at || "").getTime(),
    );
  }, [customer]);

  const primaryBillingAccount = customer?.billing_accounts?.[0] ?? null;

  const isExpiringSoon = (() => {
    if (!customer?.service_expires_at) return false;

    const diff =
      (new Date(customer.service_expires_at).getTime() - Date.now()) /
      (1000 * 60 * 60 * 24);

    return diff >= 0 && diff <= 7;
  })();

  const totalPaid = useMemo(
    () =>
      recentPayments
        .filter(
          (p) =>
            String(p.status).toLowerCase() === "completed" ||
            String(p.status).toUpperCase() === "COMPLETED",
        )
        .reduce((s, p) => s + p.amount_cents, 0),
    [recentPayments],
  );

  const totalInvoiceAmount = useMemo(
    () =>
      recentInvoices.reduce(
        (sum, invoice) => sum + invoice.total_amount_cents,
        0,
      ),
    [recentInvoices],
  );

  const paidInvoiceAmount = useMemo(
    () =>
      recentInvoices.reduce(
        (sum, invoice) => sum + invoice.paid_amount_cents,
        0,
      ),
    [recentInvoices],
  );

  if (loading) {
    return <LoadingState />;
  }

  if (error || !customer) {
    return (
      <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
        <div className="mx-auto flex min-h-[60vh] max-w-[900px] items-center justify-center">
          <div className="w-full rounded-3xl border border-rose-200 bg-rose-50 p-6 text-center text-rose-700 shadow-sm">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-rose-100">
              <AlertCircle className="h-7 w-7 text-rose-500" />
            </div>

            <p className="font-black">Customer not found</p>

            <p className="mx-auto mt-1 max-w-sm text-sm">
              {error || "The selected customer could not be loaded."}
            </p>

            <button
              type="button"
              onClick={handleBackToCustomers}
              className={`${btnBase} mt-4 h-10 border border-rose-200 bg-white px-4 text-rose-700 hover:bg-rose-100`}
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Customers
            </button>
          </div>
        </div>
      </div>
    );
  }

  const initials = getInitials(customer.username);
  const palette = avatarColor(customer.username);
  const connectionData = activeConnectionResult?.data || null;

  return (
    <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-5 sm:space-y-6">
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div
            className={`h-1.5 w-full ${
              customer.status
                ? "bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500"
                : "bg-gradient-to-r from-slate-400 to-slate-600"
            }`}
          />

          <div className="relative overflow-hidden">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan-200/35 blur-2xl" />

            <div className="relative flex flex-col gap-5 p-4 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0">
                <button
                  type="button"
                  onClick={handleBackToCustomers}
                  className="mb-3 inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-[12px] font-black text-slate-600 shadow-sm transition hover:bg-slate-100 hover:text-slate-900"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Customers
                </button>

                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10.5px] font-black uppercase tracking-[0.08em] text-emerald-700 sm:text-[11px]">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Customer Service Detail
                </div>

                <div className="mt-4 flex items-start gap-4">
                  <div
                    className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-3xl text-[19px] font-black shadow-sm ${palette}`}
                  >
                    {initials}
                  </div>

                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <h1 className="truncate text-[22px] font-black tracking-[-0.5px] text-slate-900 sm:text-[24px]">
                        {customer.username}
                      </h1>

                      <ServiceBadge active={customer.status} />
                      <ActiveConnectionBadge
                        loading={activeLookupLoading}
                        result={activeConnectionResult}
                      />
                    </div>

                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className="rounded-xl border border-slate-300 bg-slate-200 px-2.5 py-1 font-mono text-[11px] font-bold text-slate-600">
                        ACC #{customer.account_number}
                      </span>

                      <span className="rounded-xl border border-slate-200 bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-500">
                        Joined {formatDate(customer.created_at)}
                      </span>

                      <span className="rounded-xl border border-slate-200 bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-500">
                        Router {customer.router?.name || "—"}
                      </span>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2">
                      <span className="flex items-center gap-1.5 text-[13px] font-medium text-slate-600">
                        <Mail className="h-3.5 w-3.5 text-slate-400" />
                        {customer.email || "—"}
                      </span>

                      <span className="flex items-center gap-1.5 text-[13px] font-medium text-slate-600">
                        <Smartphone className="h-3.5 w-3.5 text-slate-400" />
                        {customer.phone_number || "—"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center lg:justify-end">
                <button
                  type="button"
                  onClick={() => navigate(`/customers/${customer.id}/edit`)}
                  className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                >
                  <Pencil className="h-4 w-4" />
                  Edit
                </button>

                <button
                  type="button"
                  onClick={() => setExtendOpen((value) => !value)}
                  className={`${btnBase} h-10 border px-4 shadow-sm ${
                    extendOpen
                      ? "border-amber-300 bg-amber-100 text-amber-800 hover:bg-amber-200"
                      : "border-slate-300 bg-slate-50 text-slate-600 hover:bg-slate-100 hover:text-slate-800"
                  }`}
                >
                  <Clock className="h-4 w-4" />
                  Extend
                </button>

                <button
                  type="button"
                  onClick={handleToggleStatus}
                  disabled={actionLoading}
                  className={`${btnBase} h-10 px-5 text-white shadow-lg ${
                    customer.status
                      ? "bg-gradient-to-r from-rose-500 to-red-500 shadow-rose-500/20 hover:from-rose-600 hover:to-red-600"
                      : "bg-gradient-to-r from-emerald-500 to-teal-500 shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600"
                  }`}
                >
                  {actionLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Power className="h-4 w-4" />
                  )}

                  {customer.status ? "Disable Service" : "Enable Service"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {extendOpen && (
          <div className="overflow-hidden rounded-3xl border border-amber-200 bg-amber-50/90 shadow-lg shadow-amber-200/35">
            <div className="flex items-start justify-between border-b border-amber-200/70 bg-amber-100/70 px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-amber-200 text-amber-800">
                  <Clock className="h-5 w-5" />
                </div>

                <div>
                  <p className="text-[13.5px] font-black text-amber-950">
                    Extend Internet Access
                  </p>

                  <p className="text-[11.5px] font-medium text-amber-700">
                    Grant temporary access without immediate payment.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setExtendOpen(false)}
                className="rounded-xl p-1.5 text-amber-600 transition-colors hover:bg-amber-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3 p-5 sm:grid-cols-4 sm:items-end">
              <div>
                <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-amber-800">
                  Days
                </label>

                <input
                  type="number"
                  min="1"
                  value={extendDays}
                  onChange={(e) => setExtendDays(e.target.value)}
                  className="h-10 w-full rounded-2xl border border-amber-300 bg-amber-50 px-3 text-[13px] font-semibold text-slate-800 outline-none transition-all focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-amber-800">
                  Reason
                </label>

                <input
                  type="text"
                  value={extendReason}
                  onChange={(e) => setExtendReason(e.target.value)}
                  placeholder="e.g. Promised payment on Friday"
                  className="h-10 w-full rounded-2xl border border-amber-300 bg-amber-50 px-3 text-[13px] font-semibold text-slate-800 placeholder:text-amber-500/70 outline-none transition-all focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10"
                />
              </div>

              <button
                type="button"
                onClick={handleExtendAccess}
                disabled={extendLoading}
                className={`${btnBase} h-10 w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg shadow-amber-500/20 hover:from-amber-600 hover:to-orange-600`}
              >
                {extendLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Apply Extension"
                )}
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4 xl:gap-4">
          <StatCard
            label="Balance"
            value={
              primaryBillingAccount
                ? formatCentsKES(primaryBillingAccount.balance_cents)
                : "—"
            }
            sub="Current outstanding"
            icon={<CreditCard className="h-5 w-5" />}
            tone={
              primaryBillingAccount && primaryBillingAccount.balance_cents > 0
                ? "rose"
                : "emerald"
            }
          />

          <StatCard
            label="Plan"
            value={customer.plan?.name || "No Plan"}
            sub={
              customer.amount_payable != null
                ? `${formatKES(customer.amount_payable)} / cycle`
                : customer.plan?.speed || "No plan speed"
            }
            icon={<Wifi className="h-5 w-5" />}
            tone="sky"
          />

          <StatCard
            label="Service Expiry"
            value={formatDate(customer.service_expires_at)}
            sub={
              customer.access_expires_at
                ? `Access: ${formatDate(customer.access_expires_at)}`
                : isExpiringSoon
                  ? "Expiring soon"
                  : "Normal service date"
            }
            icon={<CalendarDays className="h-5 w-5" />}
            tone={isExpiringSoon ? "amber" : "slate"}
          />

          <StatCard
            label="Live Session"
            value={
              activeLookupLoading
                ? "Checking"
                : activeConnectionResult?.active
                  ? "Online"
                  : "Offline"
            }
            sub={
              activeConnectionResult?.active
                ? connectionData?.address || "Connected"
                : activeConnectionResult?.message || "No active PPPoE session"
            }
            icon={<Activity className="h-5 w-5" />}
            tone={
              activeLookupLoading
                ? "sky"
                : activeConnectionResult?.active
                  ? "emerald"
                  : "rose"
            }
          />
        </div>

        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <InfoCard
            icon={<Smartphone className="h-5 w-5" />}
            title="Customer Contact"
            subtitle="Basic customer communication details."
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <DetailItem
                icon={<Smartphone className="h-3.5 w-3.5" />}
                label="Phone Number"
                value={customer.phone_number || "—"}
                tone="sky"
              />

              <DetailItem
                icon={<Mail className="h-3.5 w-3.5" />}
                label="Email"
                value={customer.email || "—"}
              />

              <DetailItem
                icon={<Wallet className="h-3.5 w-3.5" />}
                label="Account Number"
                value={customer.account_number || "—"}
                tone="emerald"
              />

              <DetailItem
                icon={<ShieldCheck className="h-3.5 w-3.5" />}
                label="Service Status"
                value={<ServiceBadge active={customer.status} />}
                tone={customer.status ? "emerald" : "rose"}
              />
            </div>
          </InfoCard>

          <InfoCard
            icon={<Router className="h-5 w-5" />}
            title="Service Configuration"
            subtitle="Plan, router and PPPoE login details."
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <DetailItem
                icon={<Router className="h-3.5 w-3.5" />}
                label="Router"
                value={customer.router?.name || "—"}
                tone="sky"
              />

              <DetailItem
                icon={<Wifi className="h-3.5 w-3.5" />}
                label="Plan"
                value={customer.plan?.name || "No Plan"}
                tone="emerald"
              />

              <DetailItem
                icon={<KeyRound className="h-3.5 w-3.5" />}
                label="PPPoE Username"
                value={
                  <span className="font-mono">{customer.username || "—"}</span>
                }
              />

              <DetailItem
                icon={<KeyRound className="h-3.5 w-3.5" />}
                label="PPPoE Password"
                value={
                  <span className="font-mono">
                    {customer.password || "••••••"}
                  </span>
                }
                tone="amber"
              />
            </div>
          </InfoCard>

          <InfoCard
            icon={<CreditCard className="h-5 w-5" />}
            title="Billing Summary"
            subtitle="Balance, paid amount and current billing cycle."
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <DetailItem
                icon={<Wallet className="h-3.5 w-3.5" />}
                label="Current Balance"
                value={
                  primaryBillingAccount
                    ? formatCentsKES(primaryBillingAccount.balance_cents)
                    : "—"
                }
                tone={
                  primaryBillingAccount &&
                  primaryBillingAccount.balance_cents > 0
                    ? "rose"
                    : "emerald"
                }
              />

              <DetailItem
                icon={<CreditCard className="h-3.5 w-3.5" />}
                label="Total Paid"
                value={formatCentsKES(totalPaid)}
                tone="emerald"
              />

              <DetailItem
                icon={<FileText className="h-3.5 w-3.5" />}
                label="Invoices Total"
                value={formatCentsKES(totalInvoiceAmount)}
                tone="sky"
              />

              <DetailItem
                icon={<CheckCircle2 className="h-3.5 w-3.5" />}
                label="Invoices Paid"
                value={formatCentsKES(paidInvoiceAmount)}
              />

              <DetailItem
                icon={<CalendarDays className="h-3.5 w-3.5" />}
                label="Service Expiry"
                value={formatDateTime(customer.service_expires_at)}
                tone={isExpiringSoon ? "amber" : "slate"}
              />

              <DetailItem
                icon={<Clock className="h-3.5 w-3.5" />}
                label="Access Expiry"
                value={formatDateTime(customer.access_expires_at)}
              />
            </div>
          </InfoCard>

          <InfoCard
            icon={<Activity className="h-5 w-5" />}
            title="Live Connection Status"
            subtitle="Automatically checked when this page loads."
            badge={
              <ActiveConnectionBadge
                loading={activeLookupLoading}
                result={activeConnectionResult}
              />
            }
          >
            <div className="space-y-4">
              <div className="rounded-3xl border border-slate-200 bg-slate-100/80 p-4">
                <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                  PPPoE Name / Username
                </label>

                <div className="flex flex-col gap-2 sm:flex-row">
                  <div className="relative flex-1">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                    <input
                      type="text"
                      value={activeName}
                      onChange={(e) => {
                        setActiveName(e.target.value);
                        setActiveConnectionResult(null);
                        setOnuLookupResult(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleCheckActiveConnection();
                        }
                      }}
                      placeholder="Enter PPPoE username"
                      className={`${inputCls} pl-9 font-mono font-bold`}
                    />
                  </div>

                  <button
                    type="button"
                    onClick={handleCheckActiveConnection}
                    disabled={activeLookupLoading}
                    className={`${btnBase} h-10 bg-gradient-to-r from-slate-800 to-slate-700 px-4 text-white shadow-lg shadow-slate-400/25 hover:from-slate-900 hover:to-slate-800`}
                  >
                    {activeLookupLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Activity className="h-4 w-4" />
                    )}
                    Recheck
                  </button>
                </div>

                <p className="mt-2 text-[11.5px] font-semibold text-slate-500">
                  Router: {customer.router?.name || "Assigned router"}
                </p>
              </div>

              {activeLookupLoading ? (
                <div className="rounded-3xl border border-sky-200 bg-sky-50 p-5 text-center">
                  <Loader2 className="mx-auto h-8 w-8 animate-spin text-sky-600" />

                  <p className="mt-3 text-[13.5px] font-black text-sky-900">
                    Checking active PPPoE session…
                  </p>

                  <p className="mx-auto mt-1 max-w-sm text-[12px] font-medium text-sky-700">
                    This is automatically checked when the customer page loads.
                  </p>
                </div>
              ) : activeConnectionResult?.active && connectionData ? (
                <div className="overflow-hidden rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-sm shadow-emerald-200/40">
                  <div className="flex items-start justify-between gap-3 border-b border-emerald-200/70 px-4 py-3.5">
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                        <Wifi className="h-5 w-5" />
                      </span>

                      <div>
                        <p className="text-[13.5px] font-black text-emerald-950">
                          Active Connection Found
                        </p>

                        <p className="font-mono text-[11.5px] font-bold text-emerald-700">
                          {connectionData.name || activeName}
                        </p>
                      </div>
                    </div>

                    <ActiveConnectionBadge
                      loading={false}
                      result={activeConnectionResult}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2">
                    <DetailItem
                      icon={<Wifi className="h-3.5 w-3.5" />}
                      label="Client Address"
                      value={getConnectionValue(connectionData, "address")}
                      tone="emerald"
                    />

                    <DetailItem
                      icon={<Clock className="h-3.5 w-3.5" />}
                      label="Uptime"
                      value={getConnectionValue(connectionData, "uptime")}
                    />

                    <DetailItem
                      icon={<Smartphone className="h-3.5 w-3.5" />}
                      label="Caller ID / MAC"
                      value={getConnectionValue(
                        connectionData,
                        "caller_id",
                        "caller-id",
                      )}
                    />

                    <DetailItem
                      icon={<Router className="h-3.5 w-3.5" />}
                      label="Service"
                      value={getConnectionValue(connectionData, "service")}
                    />

                    <DetailItem
                      icon={<KeyRound className="h-3.5 w-3.5" />}
                      label="Session ID"
                      value={getConnectionValue(
                        connectionData,
                        "session_id",
                        "session-id",
                      )}
                    />

                    <DetailItem
                      icon={<ShieldCheck className="h-3.5 w-3.5" />}
                      label="Radius"
                      value={getConnectionValue(connectionData, "radius")}
                    />
                  </div>

                  <div className="border-t border-emerald-200/70 px-4 py-4">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div>
                        <p className="text-[11px] font-black uppercase tracking-widest text-emerald-700">
                          OLT ONU Signal Details
                        </p>

                        <p className="mt-0.5 text-[11.5px] font-semibold text-emerald-700/80">
                          Pulled from HIOSO OLT using Caller ID / MAC.
                        </p>
                      </div>

                      {onuLookupLoading && (
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-100 px-3 py-1 text-[11px] font-black text-sky-800">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Fetching ONU
                        </span>
                      )}

                      {!onuLookupLoading &&
                        onuLookupResult?.status === "found" && (
                          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-100 px-3 py-1 text-[11px] font-black text-emerald-800">
                            <CheckCircle2 className="h-3 w-3" />
                            ONU Found
                          </span>
                        )}

                      {!onuLookupLoading &&
                        onuLookupResult &&
                        onuLookupResult.status !== "found" && (
                          <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-100 px-3 py-1 text-[11px] font-black text-amber-800">
                            <AlertCircle className="h-3 w-3" />
                            ONU Issue
                          </span>
                        )}
                    </div>

                    {onuLookupLoading ? (
                      <div className="rounded-2xl border border-sky-200 bg-sky-50 p-4 text-center">
                        <Loader2 className="mx-auto h-6 w-6 animate-spin text-sky-600" />

                        <p className="mt-2 text-[12.5px] font-black text-sky-900">
                          Fetching ONU signal from OLT…
                        </p>
                      </div>
                    ) : onuLookupResult?.status === "found" &&
                      onuLookupResult.onu ? (
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <DetailItem
                          icon={<Router className="h-3.5 w-3.5" />}
                          label="OLT"
                          value={onuLookupResult.olt_name || "—"}
                          tone="emerald"
                        />

                        <DetailItem
                          icon={<Wifi className="h-3.5 w-3.5" />}
                          label="ONU ID"
                          value={onuLookupResult.onu.id || "—"}
                          tone="sky"
                        />

                        <DetailItem
                          icon={<Smartphone className="h-3.5 w-3.5" />}
                          label="ONU MAC"
                          value={
                            <span className="font-mono">
                              {onuLookupResult.onu.mac_address ||
                                onuLookupResult.mac_address ||
                                "—"}
                            </span>
                          }
                        />

                        <DetailItem
                          icon={<ShieldCheck className="h-3.5 w-3.5" />}
                          label="ONU Status"
                          value={onuLookupResult.onu.status || "—"}
                          tone={
                            onuLookupResult.onu.status === "Up"
                              ? "emerald"
                              : "rose"
                          }
                        />

                        <DetailItem
                          icon={<Activity className="h-3.5 w-3.5" />}
                          label="Rx Power"
                          value={formatOnuUnit(
                            onuLookupResult.onu.rx_power,
                            "dBm",
                          )}
                          tone="emerald"
                        />

                        <DetailItem
                          icon={<Activity className="h-3.5 w-3.5" />}
                          label="Tx Power"
                          value={formatOnuUnit(
                            onuLookupResult.onu.tx_power,
                            "dBm",
                          )}
                          tone="sky"
                        />

                        <DetailItem
                          icon={<Clock className="h-3.5 w-3.5" />}
                          label="Distance"
                          value={formatOnuUnit(
                            onuLookupResult.onu.distance_meters,
                            "m",
                          )}
                        />

                        <DetailItem
                          icon={<Activity className="h-3.5 w-3.5" />}
                          label="Temperature"
                          value={formatOnuUnit(
                            onuLookupResult.onu.temperature,
                            "°C",
                          )}
                          tone="amber"
                        />
                      </div>
                    ) : onuLookupResult ? (
                      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
                        <p className="text-[12.5px] font-black text-amber-900">
                          ONU details unavailable
                        </p>

                        <p className="mt-1 text-[12px] font-semibold text-amber-700">
                          {onuLookupResult.error ||
                            onuLookupResult.message ||
                            "The active Caller ID / MAC was not found on the linked OLT."}
                        </p>
                      </div>
                    ) : (
                      <div className="rounded-2xl border border-slate-200 bg-slate-100 p-4">
                        <p className="text-[12px] font-semibold text-slate-500">
                          ONU details will appear here once an active connection
                          with a valid Caller ID / MAC is found.
                        </p>
                      </div>
                    )}
                  </div>

                  {connectionData.comment && (
                    <div className="border-t border-emerald-200/70 px-4 py-3">
                      <p className="text-[10.5px] font-black uppercase tracking-widest text-emerald-600">
                        Comment
                      </p>

                      <p className="mt-1 text-[13px] font-bold text-emerald-950">
                        {connectionData.comment}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded-3xl border border-rose-200 bg-rose-50 p-5 text-center shadow-sm shadow-rose-200/30">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-100 text-rose-600">
                    <XCircle className="h-5 w-5" />
                  </div>

                  <p className="mt-3 text-[13.5px] font-black text-rose-900">
                    No Active Connection
                  </p>

                  <p className="mx-auto mt-1 max-w-sm text-[12px] font-medium text-rose-700">
                    {activeConnectionResult?.message ||
                      `No active PPPoE connection found for ${activeName}.`}
                  </p>
                </div>
              )}
            </div>
          </InfoCard>
        </div>

        <InfoCard
          icon={<FileText className="h-5 w-5" />}
          title="Invoices"
          subtitle="Recent invoice records for this customer."
          badge={
            <span className="rounded-full border border-slate-300 bg-slate-50 px-2.5 py-1 text-[11px] font-bold text-slate-500">
              {recentInvoices.length} total
            </span>
          }
        >
          <div className="hidden overflow-x-auto sm:block">
            <table className="min-w-full text-left">
              <thead className="border-b border-slate-200 bg-slate-100">
                <tr>
                  {["Invoice #", "Period", "Total", "Paid", "Status"].map(
                    (h, i) => (
                      <th
                        key={h}
                        className={`px-5 py-3.5 text-[11px] font-black uppercase tracking-widest text-slate-500 ${
                          i === 4 ? "text-right" : ""
                        }`}
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200/70 bg-slate-50">
                {recentInvoices.length === 0 ? (
                  <tr>
                    <td colSpan={5}>
                      <EmptyState
                        icon={<FileText className="h-7 w-7" />}
                        title="No invoices found"
                        message="Invoices for this customer will appear here."
                      />
                    </td>
                  </tr>
                ) : (
                  recentInvoices.map((inv) => (
                    <tr
                      key={inv.id}
                      className="transition-colors hover:bg-emerald-50/60"
                    >
                      <td className="px-5 py-3.5 font-mono text-[12.5px] font-bold text-slate-700">
                        {inv.invoice_number}
                      </td>

                      <td className="px-5 py-3.5 text-[12.5px] font-medium text-slate-500">
                        {formatDate(inv.period_start)} –{" "}
                        {formatDate(inv.period_end)}
                      </td>

                      <td className="px-5 py-3.5 text-[13px] font-black text-slate-800">
                        {formatCentsKES(inv.total_amount_cents)}
                      </td>

                      <td className="px-5 py-3.5 text-[13px] font-bold text-slate-600">
                        {formatCentsKES(inv.paid_amount_cents)}
                      </td>

                      <td className="px-5 py-3.5 text-right">
                        <span
                          className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-black capitalize ${invoiceStatusStyle(
                            inv.status,
                          )}`}
                        >
                          {inv.status.toLowerCase()}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="divide-y divide-slate-200 bg-slate-50 sm:hidden">
            {recentInvoices.length === 0 ? (
              <EmptyState
                icon={<FileText className="h-7 w-7" />}
                title="No invoices found"
                message="Invoices for this customer will appear here."
              />
            ) : (
              recentInvoices.map((inv) => (
                <div
                  key={inv.id}
                  className="flex items-start justify-between gap-3 px-4 py-3.5"
                >
                  <div className="min-w-0">
                    <p className="font-mono text-[12.5px] font-black text-slate-700">
                      {inv.invoice_number}
                    </p>

                    <p className="mt-0.5 text-[11.5px] font-medium text-slate-500">
                      {formatDate(inv.period_start)} –{" "}
                      {formatDate(inv.period_end)}
                    </p>
                  </div>

                  <div className="shrink-0 text-right">
                    <p className="text-[13px] font-black text-slate-800">
                      {formatCentsKES(inv.total_amount_cents)}
                    </p>

                    <span
                      className={`mt-1 inline-flex rounded-full border px-2 py-0.5 text-[10px] font-black capitalize ${invoiceStatusStyle(
                        inv.status,
                      )}`}
                    >
                      {inv.status.toLowerCase()}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </InfoCard>

        <InfoCard
          icon={<Activity className="h-5 w-5" />}
          title="Payments"
          subtitle="Recent payment records linked to this customer."
          badge={
            <span className="rounded-full border border-slate-300 bg-slate-50 px-2.5 py-1 text-[11px] font-bold text-slate-500">
              {recentPayments.length} total
            </span>
          }
        >
          <div className="hidden overflow-x-auto sm:block">
            <table className="min-w-full text-left">
              <thead className="border-b border-slate-200 bg-slate-100">
                <tr>
                  {["Date", "Amount", "Payer", "Ref / TransID", "Status"].map(
                    (h, i) => (
                      <th
                        key={h}
                        className={`px-5 py-3.5 text-[11px] font-black uppercase tracking-widest text-slate-500 ${
                          i === 4 ? "text-right" : ""
                        }`}
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200/70 bg-slate-50">
                {recentPayments.length === 0 ? (
                  <tr>
                    <td colSpan={5}>
                      <EmptyState
                        icon={<CreditCard className="h-7 w-7" />}
                        title="No payments found"
                        message="Payments made by this customer will appear here."
                      />
                    </td>
                  </tr>
                ) : (
                  recentPayments.map((p) => {
                    const meta = p.payment_metadata || {};

                    const fullName = [
                      meta.first_name,
                      meta.middle_name,
                      meta.last_name,
                      meta.customer_name,
                    ]
                      .filter(Boolean)
                      .join(" ");

                    return (
                      <tr
                        key={p.id}
                        className="transition-colors hover:bg-emerald-50/60"
                      >
                        <td className="px-5 py-3.5 text-[12px] font-medium text-slate-500">
                          {formatDateTime(p.paid_at || p.created_at)}
                        </td>

                        <td className="px-5 py-3.5 text-[13px] font-black text-slate-800">
                          {formatCentsKES(p.amount_cents)}
                        </td>

                        <td className="px-5 py-3.5 text-[13px] font-bold text-slate-700">
                          {fullName || "—"}
                        </td>

                        <td className="px-5 py-3.5 font-mono text-[11.5px] font-bold text-slate-400">
                          {p.external_reference || "—"}
                        </td>

                        <td className="px-5 py-3.5 text-right">
                          <span
                            className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-black capitalize ${paymentStatusStyle(
                              p.status,
                            )}`}
                          >
                            {p.status.toLowerCase()}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="divide-y divide-slate-200 bg-slate-50 sm:hidden">
            {recentPayments.length === 0 ? (
              <EmptyState
                icon={<CreditCard className="h-7 w-7" />}
                title="No payments found"
                message="Payments made by this customer will appear here."
              />
            ) : (
              recentPayments.map((p) => {
                const meta = p.payment_metadata || {};

                const fullName = [
                  meta.first_name,
                  meta.middle_name,
                  meta.last_name,
                  meta.customer_name,
                ]
                  .filter(Boolean)
                  .join(" ");

                return (
                  <div
                    key={p.id}
                    className="flex items-start justify-between gap-3 px-4 py-3.5"
                  >
                    <div className="min-w-0">
                      <p className="text-[13px] font-black text-slate-800">
                        {formatCentsKES(p.amount_cents)}
                      </p>

                      <p className="mt-0.5 text-[11.5px] font-bold text-slate-500">
                        {fullName || "—"}
                      </p>

                      <p className="mt-0.5 font-mono text-[11px] font-bold text-slate-400">
                        {p.external_reference || "—"}
                      </p>
                    </div>

                    <div className="shrink-0 text-right">
                      <p className="text-[11.5px] font-medium text-slate-500">
                        {formatDateTime(p.paid_at || p.created_at)}
                      </p>

                      <span
                        className={`mt-1 inline-flex rounded-full border px-2 py-0.5 text-[10px] font-black capitalize ${paymentStatusStyle(
                          p.status,
                        )}`}
                      >
                        {p.status.toLowerCase()}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </InfoCard>
      </div>
    </div>
  );
};

export default CustomerDetailsPage;
